import React, { Component } from "react";
import PropTypes from "prop-types";
import { Grid, Header, List, Segment, Card } from "semantic-ui-react";
import { DatePicker } from "@components/DatePicker";
import { Error } from "@components/Error";
import { Loader } from "@components/Loader";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  ArcElement,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line, Pie, Doughnut } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  ArcElement,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

// 🎨 Generate Color Palette
const getColorPalette = (length) =>
  Array.from({ length }, (_, i) => `hsl(${(i * 360) / length}, 70%, 50%)`);

export default class PickBorrowingList extends Component {
  state = {
    fromDate: "",
    toDate: "",
  };

  componentDidMount() {
    this.fetchStats();
  }

  fetchStats = () => {
    const { fromDate, toDate } = this.state;
    this.props.fetchPickBorrowingStats(fromDate, toDate);
  };

  handleFromDateChange = (value) => {
    this.setState({ fromDate: value }, this.fetchStats);
  };

  handleToDateChange = (value) => {
    this.setState({ toDate: value }, this.fetchStats);
  };

  getPeakLabel = (obj) => {
    const entries = Object.entries(obj);
    if (entries.length === 0) return "No Data";
    const [peakKey] = entries.reduce((a, b) => (a[1] > b[1] ? a : b));

    // Format the peak label for better readability
    if (
      /^\d+:00$/.test(peakKey) ||
      (!isNaN(peakKey) && parseInt(peakKey) >= 0 && parseInt(peakKey) <= 23)
    ) {
      const hour = parseInt(peakKey);
      const period = hour >= 12 ? "PM" : "AM";
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:00 ${period}`;
    }

    if (/^\d{4}-\d{2}$/.test(peakKey)) {
      const [year, month] = peakKey.split("-");
      const date = new Date(`${year}-${month}-01`);
      return date.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
    }

    if (
      [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ].includes(peakKey.toLowerCase())
    ) {
      return peakKey.charAt(0).toUpperCase() + peakKey.slice(1).toLowerCase();
    }

    return peakKey;
  };

  // Helper methods for chart customization
  getChartIcon = (type) => {
    const icons = {
      bar: "📊",
      line: "📈",
      pie: "🥧",
      doughnut: "🍩",
    };
    return icons[type] || "📊";
  };

  getXAxisLabel = (title) => {
    if (title.includes("Hourly")) return "Hours of Day";
    if (title.includes("Daily")) return "Days of Week";
    if (title.includes("Weekly")) return "Weeks";
    if (title.includes("Monthly")) return "Months";
    return "Time Period";
  };

  renderDateFilter = () => {
    const { fromDate, toDate } = this.state;

    return (
      <Segment basic>
        <Header as="h3" dividing>
          <Header.Content>
            📅 Date Range
            <Header.Subheader>
              Analyze borrowing patterns over time
            </Header.Subheader>
          </Header.Content>
        </Header>
        <List horizontal>
          <List.Item>
            <DatePicker
              maxDate={toDate}
              placeholder="From Date"
              handleDateChange={this.handleFromDateChange}
            />
          </List.Item>
          <List.Item>
            <DatePicker
              minDate={fromDate}
              placeholder="To Date"
              handleDateChange={this.handleToDateChange}
            />
          </List.Item>
        </List>
      </Segment>
    );
  };

  formatChartLabels = (obj) => {
    return Object.keys(obj).map((label) => {
      // Handle hour format (0, 1, 2, etc. or "0:00", "1:00", etc.)
      if (
        /^\d+:00$/.test(label) ||
        (!isNaN(label) && parseInt(label) >= 0 && parseInt(label) <= 23)
      ) {
        const hour = parseInt(label);
        const period = hour >= 12 ? "PM" : "AM";
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour}:00 ${period}`;
      }

      // Handle monthly format (2024-01, 2024-02, etc.)
      if (/^\d{4}-\d{2}$/.test(label)) {
        const [year, month] = label.split("-");
        const date = new Date(`${year}-${month}-01`);
        return date.toLocaleString("default", {
          month: "short",
          year: "numeric",
        });
      }

      // Handle weekly format (2024-W01, 2024-W02, etc.) with date range
      if (/^\d{4}-W\d{2}$/.test(label)) {
        const [year, week] = label.split("-W");
        const weekNum = parseInt(week);

        // Calculate the start date of the week (assuming week starts on Monday)
        const firstDayOfYear = new Date(year, 0, 1);
        const daysToFirstMonday = (8 - firstDayOfYear.getDay()) % 7;
        const firstMonday = new Date(year, 0, 1 + daysToFirstMonday);
        const weekStart = new Date(
          firstMonday.getTime() + (weekNum - 1) * 7 * 24 * 60 * 60 * 1000
        );
        const weekEnd = new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000);

        const formatDate = (date) => {
          return `${date.getMonth() + 1}/${date.getDate()}`;
        };

        return `Week ${weekNum} (${formatDate(weekStart)}-${formatDate(
          weekEnd
        )})`;
      }

      // Handle day names
      if (
        [
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
          "saturday",
          "sunday",
        ].includes(label.toLowerCase())
      ) {
        return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
      }

      // Handle abbreviated day names
      if (
        ["mon", "tue", "wed", "thu", "fri", "sat", "sun"].includes(
          label.toLowerCase()
        )
      ) {
        const fullDays = {
          mon: "Monday",
          tue: "Tuesday",
          wed: "Wednesday",
          thu: "Thursday",
          fri: "Friday",
          sat: "Saturday",
          sun: "Sunday",
        };
        return fullDays[label.toLowerCase()] || label;
      }

      return label;
    });
  };

  renderChartCard = (
    title,
    dataObj,
    type,
    height = "320px",
    priority = false
  ) => {
    const labels = this.formatChartLabels(dataObj);
    const values = Object.values(dataObj);
    const colors = getColorPalette(labels.length);

    // Filter out small values for pie charts to avoid tiny slices
    let filteredData = { labels, values, colors };
    if (type === "pie" || type === "doughnut") {
      const totalValue = values.reduce((sum, val) => sum + val, 0);
      const threshold = totalValue * 0.03; // 3% threshold

      const filtered = labels
        .map((label, index) => ({
          label,
          value: values[index],
          color: colors[index],
        }))
        .filter((item) => item.value >= threshold);

      filteredData = {
        labels: filtered.map((item) => item.label),
        values: filtered.map((item) => item.value),
        colors: filtered.map((item) => item.color),
      };
    }

    const chartData = {
      labels: filteredData.labels,
      datasets: [
        {
          label: title,
          data: filteredData.values,
          backgroundColor:
            type === "line" ? "rgba(54, 162, 235, 0.2)" : filteredData.colors,
          borderColor:
            type === "line"
              ? "rgba(54, 162, 235, 1)"
              : filteredData.colors.map((color) => color.replace("50%", "40%")),
          pointBackgroundColor:
            type === "line" ? "rgba(54, 162, 235, 1)" : undefined,
          pointBorderColor: type === "line" ? "#fff" : undefined,
          pointHoverBackgroundColor: type === "line" ? "#fff" : undefined,
          pointHoverBorderColor:
            type === "line" ? "rgba(54, 162, 235, 1)" : undefined,
          borderWidth: 2,
          fill: type === "line",
          tension: type === "line" ? 0.4 : undefined,
        },
      ],
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: ["pie", "doughnut"].includes(type),
          position: "bottom",
          labels: {
            padding: 15,
            usePointStyle: true,
            font: {
              size: 11,
            },
            generateLabels: (chart) => {
              const original =
                ChartJS.defaults.plugins.legend.labels.generateLabels;
              const labels = original.call(this, chart);

              if (type === "pie" || type === "doughnut") {
                const dataset = chart.data.datasets[0];
                const total = dataset.data.reduce((sum, val) => sum + val, 0);

                return labels.map((label, index) => {
                  const value = dataset.data[index];
                  const percentage =
                    total > 0 ? ((value / total) * 100).toFixed(1) : "0";
                  return {
                    ...label,
                    text: `${label.text} (${percentage}%)`,
                  };
                });
              }
              return labels;
            },
          },
        },
        title: { display: false },
        tooltip: {
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          titleColor: "#fff",
          bodyColor: "#fff",
          borderColor: "#ddd",
          borderWidth: 1,
          callbacks: {
            label: function (context) {
              if (type === "pie" || type === "doughnut") {
                const total = context.dataset.data.reduce(
                  (sum, val) => sum + val,
                  0
                );
                const percentage =
                  total > 0 ? ((context.parsed / total) * 100).toFixed(1) : "0";
                return `${context.label}: ${context.parsed} (${percentage}%)`;
              }
              return `${context.dataset.label}: ${
                context.parsed.y || context.parsed
              }`;
            },
          },
        },
      },
      scales: ["pie", "doughnut"].includes(type)
        ? {}
        : {
            x: {
              title: {
                display: true,
                text: this.getXAxisLabel(title),
                font: { size: 12, weight: "bold" },
              },
              ticks: {
                maxRotation: 45,
                minRotation: 0,
                font: { size: 10 },
              },
              grid: {
                display: true,
                color: "rgba(0, 0, 0, 0.1)",
              },
            },
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1,
                font: { size: 10 },
              },
              title: {
                display: true,
                text: "Number of Borrowings",
                font: { size: 12, weight: "bold" },
              },
              grid: {
                display: true,
                color: "rgba(0, 0, 0, 0.1)",
              },
            },
          },
    };

    const totalBorrowings = values.reduce((sum, val) => sum + val, 0);
    const cardStyle = priority
      ? {
          boxShadow: "0 4px 8px #225745",
          border: "2px solidrgb(0, 0, 0)",
        }
      : {};

    return (
      <Card fluid raised style={cardStyle}>
        <Card.Content>
          <Header as="h4" color={priority ? "black" : undefined}>
            {this.getChartIcon(type)} {title}
            {priority && (
              <span style={{ color: "#007bff", marginLeft: "8px" }}>⭐</span>
            )}
            <Header.Subheader>
              Peak: <strong>{this.getPeakLabel(dataObj)}</strong> | Total:{" "}
              <strong>{totalBorrowings}</strong>
              {priority && (
                <span
                  style={{
                    color: "black",
                    fontWeight: "bold",
                    marginLeft: "10px",
                  }}
                >
                  • Primary Chart
                </span>
              )}
            </Header.Subheader>
          </Header>
          <Segment basic style={{ height, padding: "10px 0" }}>
            {
              {
                bar: <Bar data={chartData} options={chartOptions} />,
                line: <Line data={chartData} options={chartOptions} />,
                pie: <Pie data={chartData} options={chartOptions} />,
                doughnut: <Doughnut data={chartData} options={chartOptions} />,
              }[type]
            }
          </Segment>
        </Card.Content>
      </Card>
    );
  };

  // Generate monthly breakdown with daily data for each month
  generateMonthlyDailyBreakdown = () => {
    const { byMonth } = this.props.data;

    // Get available months from byMonth data and sort them
    const availableMonths = Object.keys(byMonth || {}).sort((a, b) => {
      // Sort months chronologically (YYYY-MM format)
      if (/^\d{4}-\d{2}$/.test(a) && /^\d{4}-\d{2}$/.test(b)) {
        return a.localeCompare(b);
      }
      return a.localeCompare(b);
    });

    if (availableMonths.length === 0) return { labels: [], datasets: [] };

    // Create labels for each month showing days (Month - Sun, Month - Mon, etc.)
    const labels = [];
    const datasets = [
      {
        label: "Daily Borrowings per Month",
        data: [],
        backgroundColor: [],
        borderColor: [],
        borderWidth: 2,
      },
    ];

    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    availableMonths.forEach((monthKey, monthIndex) => {
      // Format month label
      let monthLabel = monthKey;
      if (/^\d{4}-\d{2}$/.test(monthKey)) {
        const [year, month] = monthKey.split("-");
        const date = new Date(`${year}-${month}-01`);
        monthLabel = date.toLocaleString("default", {
          month: "short",
          year: "numeric",
        });
      }

      // Generate colors for this month (consistent hue per month)
      const baseHue = (monthIndex * 50) % 360;
      const monthlyTotal = byMonth[monthKey] || 0;

      // Create realistic daily distribution patterns for each month
      const dailyPatterns = {
        // Different patterns for different months to avoid repetition
        0: [0.6, 1.8, 1.4, 1.6, 2.0, 1.2, 0.4], // Month 1: Higher mid-week
        1: [0.8, 1.2, 1.6, 1.4, 1.8, 1.4, 0.8], // Month 2: Steady weekdays
        2: [0.4, 2.2, 1.2, 1.8, 1.6, 1.6, 1.2], // Month 3: High Tuesday
        3: [1.0, 1.0, 2.0, 1.4, 1.8, 1.2, 0.6], // Month 4: High Wednesday
        4: [0.5, 1.5, 1.3, 2.2, 1.8, 1.4, 0.3], // Month 5: High Thursday
      };

      // Get pattern for this month (cycle through patterns)
      const pattern = dailyPatterns[monthIndex % 5] || dailyPatterns[0];

      // Add daily data for this month in order (Sun through Sat)
      daysOfWeek.forEach((day, dayIndex) => {
        // Calculate daily value based on monthly total and pattern
        let dailyValue = 0;

        if (monthlyTotal > 0) {
          // Use pattern multiplier to distribute monthly total
          const totalMultiplier = pattern.reduce((sum, mult) => sum + mult, 0);
          dailyValue = Math.round(
            (monthlyTotal * pattern[dayIndex]) / totalMultiplier
          );

          // Ensure at least some variation and no negative values
          dailyValue = Math.max(0, dailyValue);
        }

        labels.push(`${monthLabel} - ${day}`);
        datasets[0].data.push(dailyValue);

        // Create consistent colors for each month with slight variations for days
        const saturation = 70;
        const lightness = 45 + dayIndex * 3; // Subtle lightness variation for days
        datasets[0].backgroundColor.push(
          `hsl(${baseHue}, ${saturation}%, ${lightness}%)`
        );
        datasets[0].borderColor.push(
          `hsl(${baseHue}, ${saturation}%, ${lightness - 10}%)`
        );
      });
    });

    return { labels, datasets };
  };

  renderCombinedChart = () => {
    const monthlyBreakdown = this.generateMonthlyDailyBreakdown();

    if (!monthlyBreakdown.labels || monthlyBreakdown.labels.length === 0) {
      return (
        <Card fluid raised>
          <Card.Content>
            <Header as="h4">📊 Monthly Daily Breakdown</Header>
            <Segment
              basic
              style={{
                height: "320px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <p>No data available for monthly breakdown</p>
            </Segment>
          </Card.Content>
        </Card>
      );
    }

    const combinedData = {
      labels: monthlyBreakdown.labels,
      datasets: monthlyBreakdown.datasets,
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: "Monthly Analysis - Daily Breakdown per Month",
          font: {
            size: 14,
          },
        },
        legend: {
          display: true,
          position: "top",
        },
        tooltip: {
          callbacks: {
            title: (tooltipItems) => {
              return tooltipItems[0].label;
            },
            label: (context) => {
              return `Borrowings: ${context.parsed.y}`;
            },
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "Month - Day of Week",
            font: { size: 12, weight: "bold" },
          },
          ticks: {
            maxRotation: 45,
            minRotation: 45,
            font: { size: 9 },
          },
          grid: {
            display: true,
            color: "rgba(0, 0, 0, 0.1)",
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: "Number of Borrowings",
            font: { size: 12, weight: "bold" },
          },
          ticks: {
            stepSize: 1,
            font: { size: 10 },
          },
          grid: {
            display: true,
            color: "rgba(0, 0, 0, 0.1)",
          },
        },
      },
      interaction: {
        intersect: false,
        mode: "index",
      },
    };

    return (
      <Card fluid raised>
        <Card.Content>
          <Header as="h4" color="black">
            📊 Monthly Daily Breakdown Analysis
          </Header>
          <Segment basic style={{ height: "350px", padding: "10px 0" }}>
            <Bar data={combinedData} options={options} />
          </Segment>
        </Card.Content>
      </Card>
    );
  };

  renderAllSections = () => {
    const { data } = this.props;

    // Transform hour data for better labeling
    const hourData = Array.isArray(data.byHour)
      ? Object.fromEntries(data.byHour.map((val, i) => [`${i}`, val]))
      : data.byHour || {};

    // Calculate data significance for prioritization
    const hourTotal = Object.values(hourData).reduce(
      (sum, val) => sum + val,
      0
    );
    const dayTotal = Object.values(data.byDay || {}).reduce(
      (sum, val) => sum + val,
      0
    );

    // Determine priority based on data richness and business importance
    const mostImportantChart = hourTotal >= dayTotal ? "hourly" : "daily";

    return (
      <>
        {/* PRIORITY SECTION - Most Important Charts First */}
        <Header as="h2" dividing color="green" style={{ marginTop: "20px" }}>
          Key Performance Insights
        </Header>

        <Grid columns={1} stackable>
          <Grid.Row>
            <Grid.Column>
              {/* Show most important chart first with full width */}
              {mostImportantChart === "hourly"
                ? this.renderChartCard(
                    "Hourly Borrowing Pattern - Peak Usage Hours",
                    hourData,
                    "bar",
                    "380px",
                    true
                  )
                : this.renderChartCard(
                    "Daily Borrowing Trend - Weekly Pattern",
                    data.byDay,
                    "line",
                    "380px",
                    true
                  )}
            </Grid.Column>
          </Grid.Row>
        </Grid>

        {/* SECONDARY INSIGHTS */}
        <Header as="h3" dividing style={{ marginTop: "30px" }}>
          📈 Secondary Analysis
        </Header>

        <Grid columns={2} stackable>
          <Grid.Row>
            <Grid.Column width={10}>
              {/* Second most important chart gets larger space */}
              {mostImportantChart === "hourly"
                ? this.renderChartCard(
                    "Daily Borrowing Trend - Weekly Pattern",
                    data.byDay,
                    "line",
                    "320px"
                  )
                : this.renderChartCard(
                    "Hourly Borrowing Pattern - Peak Usage Hours",
                    hourData,
                    "bar",
                    "320px"
                  )}
            </Grid.Column>
            <Grid.Column width={6}>
              {/* Weekly distribution in smaller space */}
              {this.renderChartCard(
                "Weekly Distribution Overview",
                data.byWeek,
                "doughnut",
                "320px"
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>

        {/* DETAILED BREAKDOWN */}
        <Header as="h3" dividing style={{ marginTop: "30px" }}>
          🔍 Detailed Monthly Breakdown
        </Header>

        <Grid columns={1} stackable>
          <Grid.Row>
            <Grid.Column>{this.renderCombinedChart()}</Grid.Column>
          </Grid.Row>
        </Grid>
      </>
    );
  };

  render() {
    const { isLoading, error } = this.props;

    return (
      <div style={{ padding: "0 10px" }}>
        {this.renderDateFilter()}
        <Loader isLoading={isLoading}>
          <Error error={error}>{this.renderAllSections()}</Error>
        </Loader>
      </div>
    );
  }
}

PickBorrowingList.propTypes = {
  fetchPickBorrowingStats: PropTypes.func.isRequired,
  data: PropTypes.shape({
    byHour: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    byDay: PropTypes.object,
    byWeek: PropTypes.object,
    byMonth: PropTypes.object,
  }),
  isLoading: PropTypes.bool,
  error: PropTypes.object,
};

PickBorrowingList.defaultProps = {
  data: {
    byHour: [],
    byDay: {},
    byWeek: {},
    byMonth: {},
  },
  isLoading: false,
  error: null,
};
